// Activity log building blocks (core): one line per action, the 7/30/60/90 days picker and the count tiles.
// Used by A2 (Statistics card + Recent activity) and A2.2 (Activity page).
import type { ActivityCounts, ActivityItem, IssueStatus } from "../api/types";
import { formatDate } from "../format";
import { href, type Route } from "../router";
import { Seg, StatusPill } from "./kit";

const VERB: Record<string, string> = { created: "added", logged: "logged", status: "changed", updated: "updated", voided: "voided", migrated: "migrated", published: "published", signed: "signed off" };
const DOT: Record<string, string> = { created: "#EF9F27", logged: "#8f8e88", status: "#378ADD", updated: "#8f8e88", voided: "#888780", migrated: "#7F77DD", published: "#D85A30", signed: "#1D9E75" };
export const MODULE_NAME: Record<string, string> = { issues: "Issues Tracker", progress: "Progress reporting" };

/** Activity types for the A2.2 "All types" filter (value = API type). */
export const ACTIVITY_TYPES = [
  { value: "added", label: "Issue added" }, { value: "logged", label: "Log entry" }, { value: "status", label: "Status changed" },
  { value: "updated", label: "Issue edited" }, { value: "voided", label: "Issue voided" }, { value: "migrated", label: "Issue migrated" },
  { value: "published", label: "Report published" }, { value: "signed", label: "Signed off" },
];

/** Status-change rows carry "Open → Closed" in detail; the new status is shown as a pill. */
export function newStatusOf(a: ActivityItem): IssueStatus | null {
  if (a.verb !== "status") return null;
  const to = a.detail.split("→").pop()?.trim();
  return to === "Open" || to === "Critical" || to === "Closed" || to === "Voided" ? to : null;
}

/**
 * Where the bold subject of an activity line links to (comment #7, 03/Oct/26):
 * issue rows → that issue (A5); migrated → the new issue; report versions (published / signed off) →
 * A3.2 Publish history opened at that component (Part 5).
 */
export function activityLink(a: ActivityItem, code: string): Route | null {
  if (a.module_id !== "issues") return null;
  if (a.verb === "published" || a.verb === "signed") {
    const prefix = a.component ?? a.subject.split(" ")[0];
    return prefix ? { name: "issues", code, history: prefix } : null;
  }
  const issue = (a.verb === "migrated" ? a.subject.split("→").pop() : a.subject)?.trim() ?? "";
  const m = /^([A-Z][A-Z0-9]{1,3})-\d{2,}$/.exec(issue);
  return m ? { name: "issue", code, prefix: m[1], issue } : null;
}

export function ActivityLine({ a, wide = false, meta = true, code }: { a: ActivityItem; wide?: boolean; meta?: boolean; code?: string }) {
  const to = newStatusOf(a);
  const detail = a.verb === "status" ? (wide ? `was ${a.detail.split("→")[0].trim()}` : "") : wide ? a.detail : "";
  return (
    <div className="act-line" data-verb={a.verb}>
      <span className="act-dot" style={{ background: DOT[a.verb] ?? "#c9c6bc" }} />
      <div className="act-text">
        <p>{a.actor ?? "Someone"} {VERB[a.verb] ?? a.verb} {subjectLink(a, code)}{to && <> to <StatusPill status={to} /></>}
          {detail && <span className="muted"> — {detail}</span>}</p>
        {meta && <p className="act-meta">{MODULE_NAME[a.module_id] ?? a.module_id}{a.component && <> · <span className="code">{a.component}</span></>} · {formatDate(a.created_at)}</p>}
      </div>
    </div>
  );
}

function subjectLink(a: ActivityItem, code?: string) {
  const r = code ? activityLink(a, code) : null;
  return r ? <a className="act-link" href={href(r)}>{a.subject}</a> : <b>{a.subject}</b>;
}

export type Period = 7 | 30 | 60 | 90;
export function PeriodSeg({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return <Seg label="Activity period" value={String(value) as "7" | "30" | "60" | "90"} onChange={(v) => onChange(Number(v) as Period)}
    options={[{ value: "7", label: "7 days" }, { value: "30", label: "30" }, { value: "60", label: "60" }, { value: "90", label: "90" }]} />;
}

const TILES: [keyof ActivityCounts, string][] = [["logged", "Issues logged"], ["added", "Issues added"], ["published", "Reports published"], ["progress", "Progress updates"]];
export function ActivityTiles({ counts, progressOn = false }: { counts: ActivityCounts; progressOn?: boolean }) {
  return <>{TILES.map(([k, label]) => (
    <div key={k} className="act-tile" data-act={k}>
      <p>{label}</p><p className={`n${k === "progress" && !progressOn ? " off" : ""}`}>{k === "progress" && !progressOn ? "–" : counts[k]}</p>
    </div>))}</>;
}
